/**
 * The two host seams every `LonaSdk.Client` is constructed with besides its
 * storage, platform and sink: what executes its durable (debounced) writes —
 * and so where their refusals come from — (`debounceExecutor`), and what
 * happens to a failure a tolerant request swallowed
 * (`requestErrorHandler`). Each is a required `ClientConfig` field with no
 * default: a host states each decision at the call site, choosing one of
 * the values below (or `LonaSdk.Client.headlessScript`, which states both
 * for a script). The SDK raises no warning or undo offer of its own: a
 * delete resolves the `Deletion` its caller builds a notice from.
 *
 * One naming pattern for both: a singular type (`DebounceExecutor`,
 * `RequestErrorHandler`) and a plural namespace of the shipped values
 * (`DebounceExecutors`, `RequestErrorHandlers`); the `ClientConfig` field
 * is named after the type.
 *
 * ```ts
 * // A browser host: the service worker runs debounced writes and reports back.
 * new LonaSdk.Client({
 *   ...,
 *   debounceExecutor: DebounceExecutors.browser(DOM_DEBOUNCE_BRIDGE),

 *   requestErrorHandler: RequestErrorHandlers.TRACE,
 * });
 * // A CLI or server-side host: writes run in this process, failures throw.
 * using client = new LonaSdk.Client({
 *   ...,
 *   debounceExecutor: DebounceExecutors.inProcess(),
 *   requestErrorHandler: RequestErrorHandlers.THROW,
 * });
 * ```
 */
import type { Option, Unsubscribe } from "@tento-lona/sheets";
import { DebouncedRequests } from "@tento-core/debounced-requests";
import { ListenerErrors } from "@tento-core/listener-errors";
import { Tracing } from "@tento-core/tracing";
import {
  FetchConfig,
  type ResponseError,
  TypedApi,
} from "@tento-core/typed-api";
import { SdkRequestError } from "@tento-lona/sheets";
import { deepFrozen } from "@tento-lona/sheets/internal-sdk";

/**
 * What happens to a failure a tolerant request swallowed
 * (`ClientConfig.requestErrorHandler`). The SDK's required requests reject
 * with a typed `SdkRequestError`; its tolerant ones (the cell-range reads a
 * sheet renders from, which read empty on failure) resolve `undefined` and
 * hand the failure to this handler, as an `SdkRequestError` with boundary
 * `"request"` and step `"tolerate-failure"`.
 */
export type RequestErrorHandler = (error: SdkRequestError) => void;

/** The shipped `RequestErrorHandler`s. */
export namespace RequestErrorHandlers {
  /** Throw the error from the tolerant call, which then rejects instead of
   *  reading empty. For CLIs, server-side hosts, examples and tests: a
   *  silent 4xx/5xx fails the run. */
  export const THROW: RequestErrorHandler = (error) => {
    throw error;
  };

  /** Trace the error (`net`) and continue: the tolerant call reads empty.
   *  For the browser, where an empty range re-renders on the next read. */
  export const TRACE: RequestErrorHandler = (error) => {
    Tracing.warn("net")(`tolerant request failed: ${error.message}`);
  };
}

/**
 * What happens to an error a subscriber threw (`ClientConfig.listenerErrorHandler`):
 * a cell or row change listener, a sync-state listener, a store bridge. A
 * listener is never part of the fetch or write that notified it, so its throw
 * reaches this handler instead — after every other listener ran — as thrown
 * (several from one notification arrive as one `AggregateError`).
 */
export type ListenerErrorHandler = (error: ListenerErrorHandler.Reason) => void;

/** Types that go with `ListenerErrorHandler`: `Reason`. */
export namespace ListenerErrorHandler {
  /** What a listener threw: any value (the untyped rejection-reason type). */
  export type Reason = Parameters<NonNullable<Parameters<Promise<void>["then"]>[1]>>[0];
}

/** The shipped `ListenerErrorHandler`s. */
export namespace ListenerErrorHandlers {
  /** Raise it as the host's uncaught error on the next microtask (never into
   *  the notifier): the browser reports it and the page lives; a Deno host
   *  with no global `error` listener exits. The default. */
  export const RETHROW: ListenerErrorHandler = ListenerErrors.RETHROW;

  /**
   * Report it and continue: for a CLI or TUI, where an uncaught error would
   * end the process. Never silent: it is written to stderr
   * (`console.error`) whether or not a tracing logger is installed, and
   * traced (`ui`) for an installed one. Several listeners' errors from one
   * notification (an `AggregateError`) are written with each inner error. A
   * repeating throw is written once, then counted (a note at its 10th, 100th,
   * … time): an error seen before is the same object, or an `Error` (or
   * primitive) with the same text; a thrown non-`Error` object repeats only
   * as the same object. A client given this handler keeps its own memory of
   * what it wrote (the last 256 distinct texts); called directly, the memory
   * is this constant's.
   */
  export const TRACE: ListenerErrorHandler = reportOnce(256);

  /**
   * One reported error as a one-line diagnostic: `Name: message` for an
   * `Error`, else its string. For a host that collects listener errors
   * itself (a CLI's log, a test's drive host) instead of using `TRACE`.
   */
  export function describe(error: ListenerErrorHandler.Reason): string {
    return error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  }
}

/**
 * A fresh `ListenerErrorHandlers.TRACE` with its own memory: what a client
 * given `TRACE` reports through, so one client's errors never hide
 * another's. SDK-internal: not on the barrel.
 */
export function traceListenerErrors(): ListenerErrorHandler {
  return reportOnce(256);
}

/** A handler that writes each distinct error once and counts its repeats (see `ListenerErrorHandlers.TRACE`); `capacity` bounds the texts it remembers. */
function reportOnce(capacity: number): ListenerErrorHandler {
  type Seen = { count: number };
  const seenObjects = new WeakMap<object, Seen>();
  const seenTexts = new Map<string, Seen>();
  const write = (line: string): void => {
    console.error(line);
    Tracing.warn("ui")(line);
  };
  return (error) => {
    const text = describeThrown(error);
    const isObject = typeof error === "object" && error !== null;
    // A non-Error object's text is not its identity ("[object Object]" for all).
    const byText = !isObject || error instanceof Error;
    const seen = (isObject ? seenObjects.get(error) : undefined) ?? (byText ? seenTexts.get(text) : undefined);
    if (seen != null) {
      seen.count++;
      if (isObject) seenObjects.set(error, seen);
      if (isPowerOfTen(seen.count)) write(`a listener threw again (${seen.count} times): ${text.split("\n")[0]}`);
      return;
    }
    const fresh: Seen = { count: 1 };
    if (isObject) seenObjects.set(error, fresh);
    if (byText) {
      seenTexts.set(text, fresh);
      if (seenTexts.size > capacity) {
        const oldest = seenTexts.keys().next();
        if (!oldest.done) seenTexts.delete(oldest.value);
      }
    }
    write(`a listener threw: ${text}`);
  };
}

/** Whether `n` is 10, 100, 1000, …: when a repeat count is written. */
function isPowerOfTen(n: number): boolean {
  if (n < 10) return false;
  while (n % 10 === 0) n /= 10;
  return n === 1;
}

/**
 * What a listener threw, as text: an `Error`'s stack, followed by each inner
 * error of an `AggregateError` (several listeners threw); a non-`Error`
 * object as JSON when it has one; anything else as `String`.
 */
function describeThrown(error: ListenerErrorHandler.Reason): string {
  if (error instanceof Error) {
    const head = error.stack ?? `${error.name}: ${error.message}`;
    if (!(error instanceof AggregateError)) return head;
    const inner = error.errors.map((each, i) =>
      `\n  [${i + 1}/${error.errors.length}] ${describeThrown(each).replaceAll("\n", "\n    ")}`
    );
    return head + inner.join("");
  }
  if (typeof error === "object" && error !== null) {
    let json: Option<string> = null;
    try {
      json = JSON.stringify(error) ?? null;
    } catch {
      json = null;
    }
    return `a non-Error ${Object.prototype.toString.call(error)}${json == null ? "" : ` ${json}`}`;
  }
  return String(error);
}

/**
 * The `SdkRequestError` a tolerant request's failure is reported as
 * (`RequestErrorHandler`). SDK-internal: the client's typed-API delegate
 * builds it; not on the barrel.
 */
export function tolerantRequestError(
  failure: ResponseError,
  request: TypedApi.RequestLine,
): SdkRequestError {
  const verdict = SdkRequestError.answered(failure.code);
  return new SdkRequestError({
    boundary: "request",
    step: "tolerate-failure",
    method: request.method,
    path: request.route,
    failure: verdict,
    reason: failure.reason,
    details: failure.details,
  });
}

/**
 * What executes a client's debounced (durable) writes, and the source of
 * their refusals. A durable write (`events.updateDurable`,
 * `row.updateDurable`, a writable facade's `writeDurable`,

 * `sheets.updateDurable`) is a handoff to the executor, which returns no
 * response; a refusal reaches the client only through the executor's own
 * `failures`. So the executor that runs the writes is the one that reports
 * them: no host can pair a reporting executor with a deaf source.
 *
 * Bound once, by the client's constructor, to the client's origin and
 * token; the client routes every refusal the binding reports (a durable
 * write still in flight takes its change back and its `settled` rejects; a
 * refused row or sheet update re-reads it; the rest reaches
 * `client.onUnclaimedDurableFailure`) and releases the binding on
 * `dispose`.
 */
export interface DebounceExecutor {
  bind(host: DebounceExecutor.Host): DebounceExecutor.Binding;
}

/**
 * Types that go with `DebounceExecutor`: `Host`, `PageBridge`, `Failure`,
 * `Failures`, `Binding`.
 */
export namespace DebounceExecutor {
  /** What a client hands its executor at construction. */
  export type Host = {
    readonly baseUrl: Option<string>;
    readonly accessToken: Option<string>;
    /** The client's transport (`ClientConfig.fetch`); `null`: the global
     *  `fetch`. An in-process executor sends with it; an executor that
     *  cannot (the page's) refuses to bind a non-`null` one. */
    readonly fetch: Option<typeof fetch>;
  };

  /**
   * What installs the page's debounce executor (`TypedApi.installDebounce`)
   * for `DebounceExecutors.browser(bridge)`: a browser host passes the DOM's
   * (`DOM_DEBOUNCE_BRIDGE` from `@tento/lona/sheets-ui` — the service
   * worker when one controls the page, else a timer fallback; the host's
   * service-worker start replaces it with the worker bridge). The executor
   * the client routes to is decided by this argument alone, never by the
   * platform.
   */
  export interface PageBridge {
    /** Install the page's executor, sending with `baseUrl` / `accessToken`. */
    install(baseUrl: Option<string>, accessToken: Option<string>): void;
  }

  /** One refused (or unanswered, status `0`) debounced request, as its
   *  executor observed it. */
  export type Failure = DebouncedRequests.Failure;

  /** Where a binding's refusals are delivered. */
  export interface Failures {
    /** Deliver every refusal this binding observes to `route` from now on,
     *  until the returned unsubscribe runs. */
    subscribe(route: (failure: Failure) => void): Unsubscribe;
  }

  /** An executor bound to one client. */
  export type Binding = {
    /** Where the client's typed API hands each debounced request. */
    readonly queue: TypedApi.DebounceQueue;
    /** The refusals of the requests `queue` ran. */
    readonly failures: Failures;
    /** Give the binding back (`LonaSdk.Client.dispose`). Idempotent. */
    release(): void;
  };
}

/** The shipped `DebounceExecutor`s. */
export namespace DebounceExecutors {
  /** The page executor's current binding (see `browser`). */
  let page: Option<{
    readonly baseUrl: Option<string>;
    readonly accessToken: Option<string>;
    clients: number;
  }> = null;

  /**
   * The page's executor: `bridge.install` installs it when a client binds
   * (the DOM bridge installs the service worker's executor, or its timer
   * fallback; the host's service-worker start replaces it with the worker
   * bridge), and each request goes to whatever is installed when it is sent
   * (`TypedApi.installDebounce`).
   * Refusals come from the page's debounced-request channel
   * (`DebouncedRequests`), which the bridge and the fallback report into;
   * each client claims only its own writes.
   *
   * PAGE-SINGLETON: there is one installed executor per page (per process),
   * bound to one origin and token. Every live client built on `browser()`
   * must share them; binding a second client with a different `baseUrl` or
   * `accessToken` while an earlier one is live (not yet `dispose`d) throws,
   * instead of silently sending the first client's writes with the second
   * client's token. A host that needs clients on two origins uses
   * `inProcess()`.
   *
   * The page's executor sends with the page's own transport, so a client
   * with its own `ClientConfig.fetch` cannot be honoured: binding one
   * throws instead of silently sending past it.
   */
  export function browser(bridge: DebounceExecutor.PageBridge): DebounceExecutor {
    return {
      bind(host) {
        if (host.fetch != null) {
          throw new Error(
            "DebounceExecutors.browser(bridge): the page's debounce executor " +
              "sends with the page's own transport, so `ClientConfig.fetch` " +
              "cannot be honoured — pass `fetch: null`, or use " +
              "DebounceExecutors.inProcess(), which sends with it",
          );
        }
        if (
          page != null && page.clients > 0 &&
          (page.baseUrl !== host.baseUrl ||
            page.accessToken !== host.accessToken)
        ) {
          throw new Error(
            "DebounceExecutors.browser(): the page's debounce executor is " +
              `already bound to ${page.baseUrl ?? "the page's origin"} ` +
              "with another token by a live client; dispose that client " +
              "first, or give each origin its own DebounceExecutors.inProcess()",
          );
        }
        if (page == null || page.clients === 0) {
          page = {
            baseUrl: host.baseUrl,
            accessToken: host.accessToken,
            clients: 0,
          };
        }
        const bound = page;
        bound.clients++;
        bridge.install(host.baseUrl, host.accessToken);

        let released = false;
        return {
          queue: (config) => TypedApi.queueInstalledDebounce(config),
          failures: {
            subscribe: (route) => DebouncedRequests.subscribe(route),
          },
          release() {
            if (released) return;
            released = true;
            bound.clients--;
          },
        };
      },
    };
  }

  /**
   * Run each debounced request in this process, at once, against the
   * client's origin with its token and transport (`TypedApi.sendSerialized`). A refused
   * answer (`DebouncedRequests.classify`: a non-2xx, or a 2xx that is a
   * followed redirect to a page), or no answer (status `0`), is reported to THIS binding naming
   * the request (`requestIds`) before the handoff resolves. Per client:
   * two in-process clients never hear each other's refusals. For a CLI, a
   * server-side host or a test.
   */
  export function inProcess(): DebounceExecutor {
    return {
      bind(host) {
        const failures = new FailureChannel();
        return {
          queue: (config) => runInProcess(config, host, failures),
          failures,
          release: () => failures.close(),
        };
      },
    };
  }

  /** No executor: every debounced request is refused at once (reported as
   *  status 0, "no debounce executor"), so every durable write's `settled`
   *  rejects. For a read-only host that must never send a durable write. */
  export const NONE: DebounceExecutor = deepFrozen({
    bind() {
      const failures = new FailureChannel();
      return {
        queue: (config) => {
          failures.report(failure(config, 0, null, "no debounce executor"));
        },
        failures,
        release: () => failures.close(),
      };
    },
  });

  /** One binding's own refusal channel (`inProcess`, `NONE`). */
  class FailureChannel implements DebounceExecutor.Failures {
    readonly #routes = new Set<(failure: DebounceExecutor.Failure) => void>();

    subscribe(route: (failure: DebounceExecutor.Failure) => void): Unsubscribe {
      this.#routes.add(route);
      return () => {
        this.#routes.delete(route);
      };
    }

    report(failure: DebounceExecutor.Failure): void {
      if (this.#routes.size === 0) {
        Tracing.info("net")(
          `debounced-request-failed ${failure.status} ${failure.url}`,
        );
        return;
      }
      for (const route of [...this.#routes]) route(failure);
    }

    close(): void {
      this.#routes.clear();
    }
  }

  async function runInProcess(
    config: FetchConfig.Debounce,
    host: DebounceExecutor.Host,
    failures: FailureChannel,
  ): Promise<void> {
    let response: Response;
    try {
      response = await TypedApi.sendSerialized(
        config,
        host.baseUrl,
        host.accessToken,
        host.fetch,
      );
    } catch (err) {
      failures.report(failure(config, 0, null, String(err)));
      return;
    }
    // The executors' one reading of an answer: a non-2xx, and a 2xx that is
    // a followed redirect to a page (or an HTML page), are refusals.
    const refusal = await DebouncedRequests.classify(response);
    if (refusal == null) {
      await response.body?.cancel();
      return;
    }
    failures.report(
      failure(config, refusal.status, refusal.reason ?? null, refusal.details ?? null),
    );
  }

  function failure(
    config: FetchConfig.Debounce,
    status: number,
    reason: Option<string>,
    details: Option<string>,
  ): DebounceExecutor.Failure {
    return {
      customKey: config.customKey,
      requestIds: config.requestId == null ? null : [config.requestId],
      url: FetchConfig.resolve(config.route, config.config).resolvedRoute,
      status,
      reason,
      details,
    };
  }
}
